import type { PublicUser } from './types';
import { digits, fold, type SortDirection } from './sorting';

/**
 * Finding and ordering a register.
 *
 * In the browser, over the students already fetched: the server hands back a
 * whole centre's register in one response up to its two-thousand-row ceiling,
 * and filtering a list that is already in hand beats a round trip for every
 * keystroke. Past that ceiling the server logs that it needs paging, and this
 * is the thing that would move with it.
 */
export type StudentSortKey = 'candidate' | 'name' | 'phone' | 'group';

/**
 * A search that matches what a teacher has in front of them.
 *
 * Three of them, really: the name they are looking at, the candidate number
 * printed on a script, and the phone number a student reads out — which they
 * will read out as "90 111 11 01" whatever way it happens to be stored. So
 * anything with a digit in it is compared as digits alone.
 */
export function searchStudents(students: PublicUser[], query: string): PublicUser[] {
  const text = fold(query);
  if (!text) return students;
  const number = digits(query);

  return students.filter((student) => {
    if (fold(student.fullName).includes(text)) return true;
    // A number typed with spaces, dashes or a country code all reduce to the
    // same thing, and a bare "90 111" is a prefix of the stored number.
    if (number.length > 0) {
      if (digits(student.phone).includes(number)) return true;
      if (digits(student.candidateNumber).includes(number)) return true;
    }
    return false;
  });
}

/** Students with no class sort together, after the classes, whichever way round. */
const NO_CLASS = '￿';

const compare = (a: PublicUser, b: PublicUser, key: StudentSortKey): number => {
  switch (key) {
    case 'candidate':
      return digits(a.candidateNumber).localeCompare(digits(b.candidateNumber), undefined, {
        numeric: true,
      });
    case 'name':
      return a.fullName.localeCompare(b.fullName);
    case 'phone':
      return digits(a.phone).localeCompare(digits(b.phone));
    case 'group':
      return (a.groupName ?? NO_CLASS).localeCompare(b.groupName ?? NO_CLASS);
  }
};

/**
 * Sorted, and never ambiguously so: a register sorted by class is mostly ties,
 * and without a fallback the rows inside a class would reshuffle every time
 * the column was clicked.
 */
export function sortStudents(
  students: PublicUser[],
  key: StudentSortKey,
  direction: SortDirection,
): PublicUser[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...students].sort((a, b) => {
    const first = compare(a, b, key);
    if (first !== 0) return first * sign;
    return a.fullName.localeCompare(b.fullName);
  });
}

export const arrangeStudents = (
  students: PublicUser[],
  options: { query: string; key: StudentSortKey; direction: SortDirection },
): PublicUser[] =>
  sortStudents(searchStudents(students, options.query), options.key, options.direction);
